import { useCallback, useEffect, useRef, useState } from "react";
import { OrderApi } from "../../services";
import { useRequestLock } from "../../shared/hooks/useRequestLock";
import {
  buildOrderCostReviewPayload,
  getAllowedOrderCostReviewStatuses,
  isOrderCostReviewPayloadAllowed,
  mapAllEditableNodesToPayload,
  normalizeDetailFieldValue,
  setByPath,
  shouldAdvanceOrderCostReview,
  updateTreeById,
} from "./helpers";
import { checkPermissions } from "../../utils/checkPermissions";
import {
  OrderCostDetail,
  OrderCostDetailState,
  OrderCostReviewer,
} from "./types";

type UseOrderCostSheetParams = {
  messageApi: any;
  features: any;
  onOrderCostStatusChange?: (orderId: string, costStatus?: string) => void;
};

export const useOrderCostSheet = ({
  messageApi,
  features,
  onOrderCostStatusChange,
}: UseOrderCostSheetParams) => {
  const featuresRef = useRef(features);
  featuresRef.current = features;
  const canViewIncurred = checkPermissions(
    "INCURRED",
    "view",
    features,
  );
  const [dataDetail, setDataDetail] = useState<OrderCostDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const activeDetailIdRef = useRef<string | null>(null);
  const detailRequestVersionRef = useRef(0);
  const { isLocked, runLocked } = useRequestLock();
  const isSavingDetail = () =>
    Boolean(
      dataDetail?._id &&
        isLocked(`order-cost-sheet:save:${dataDetail._id}`),
    );
  const savingDetail = isSavingDetail();

  const closeDetail = useCallback(() => {
    activeDetailIdRef.current = null;
    detailRequestVersionRef.current += 1;
    setLoadingDetail(false);
    setDataDetail(null);
  }, []);

  useEffect(() => {
    if (canViewIncurred) return;
    closeDetail();
  }, [canViewIncurred, closeDetail]);

  const reloadOrderCostSheet = async (
    id: string,
    fallbackState?: OrderCostDetailState,
  ) => {
    if (
      !checkPermissions("INCURRED", "view", featuresRef.current) ||
      activeDetailIdRef.current !== id
    ) {
      return;
    }

    const requestVersion = detailRequestVersionRef.current + 1;
    detailRequestVersionRef.current = requestVersion;
    const isCurrentRequest = () =>
      checkPermissions("INCURRED", "view", featuresRef.current) &&
      activeDetailIdRef.current === id &&
      detailRequestVersionRef.current === requestVersion;

    setLoadingDetail(true);
    try {
      const response = await OrderApi.getOrderCostSheet(id);
      if (!isCurrentRequest()) return;

      const nextCostStatus = response?.order?.costStatus;
      onOrderCostStatusChange?.(id, nextCostStatus);
      setDataDetail({
        _id: id,
        onEdit: false,
        ...(response.costSheet || {}),
        state: nextCostStatus || fallbackState,
      });
    } catch (error: any) {
      if (!isCurrentRequest()) return;

      messageApi.error(
        error?.message || "Lấy chi tiết đơn hàng thất bại",
      );
    } finally {
      if (isCurrentRequest()) setLoadingDetail(false);
    }
  };

  const handleOnEditDetail = async (
    id: string,
    state?: OrderCostDetailState,
  ) => {
    if (
      !checkPermissions("INCURRED", "view", featuresRef.current)
    ) {
      return;
    }
    if (isSavingDetail()) return;

    activeDetailIdRef.current = id;
    setDataDetail(null);
    await reloadOrderCostSheet(id, state);
  };

  const handleOnChangeDetail = (
    configId: string | number,
    dataIndex: string | (string | number)[],
    value: any,
  ) => {
    const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
    const normalizedValue = normalizeDetailFieldValue(value);
    const touchedKey = path.join(".");
    const isReviewChange = path[0] === "reviews";

    if (!isReviewChange) {
      if (
        !checkPermissions("INCURRED", "edit", featuresRef.current)
      ) {
        return;
      }
    } else {
      const reviewer = path[1] as OrderCostReviewer;
      if (reviewer !== "sale" && reviewer !== "manager") return;
      const expectedState =
        reviewer === "sale"
          ? "WAITING_APPROVE_L1"
          : "WAITING_APPROVE_L2";
      const canApprove = checkPermissions(
        "INCURRED",
        reviewer === "sale" ? "approve" : "approve_l2",
        featuresRef.current,
      );
      const canReject = checkPermissions(
        "INCURRED",
        "reject",
        featuresRef.current,
      );

      if (dataDetail?.state !== expectedState) return;
      if (
        path[2] === "status" &&
        !getAllowedOrderCostReviewStatuses({
          canApprove,
          canReject,
        }).includes(normalizedValue)
      ) {
        return;
      }
      if (path[2] === "note" && !canReject) return;
      if (path[2] !== "status" && path[2] !== "note") return;
    }

    setDataDetail((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        onEdit: true,
        lines: updateTreeById(prev.lines ?? [], configId, (node) => {
          const nextNode = setByPath(node, path, normalizedValue);
          return {
            ...nextNode,
            touchedFields: {
              ...(nextNode.touchedFields || {}),
              [touchedKey]: true,
            },
          };
        }),
      };
    });
  };

  const handleSaveDetail = async () => {
    if (
      !checkPermissions("INCURRED", "edit", featuresRef.current)
    ) {
      return;
    }
    if (!dataDetail?._id) return;

    const targetId = dataDetail._id;
    const fallbackState = dataDetail.state;
    const payload = mapAllEditableNodesToPayload(dataDetail.lines ?? []);
    if (!payload.changes.length) {
      setDataDetail((prev) => (prev ? { ...prev, onEdit: false } : prev));
      return;
    }

    try {
      await runLocked(`order-cost-sheet:save:${targetId}`, async () => {
        if (
          !checkPermissions(
            "INCURRED",
            "edit",
            featuresRef.current,
          )
        ) {
          return;
        }
        await OrderApi.patchUpdateOrderCost(targetId, payload);
        await reloadOrderCostSheet(targetId, fallbackState);
      });
    } catch (error: any) {
      messageApi.error(error?.message || "Lưu thay đổi thất bại");
    }
  };

  const ensureDetailSavedBeforeSubmit = () => {
    if (!dataDetail?.onEdit) return true;
    messageApi.warning("Vui lòng lưu thay đổi trước khi chuyển duyệt");
    return false;
  };

  const handleSubmitOrderCostLevel1 = async () => {
    if (
      !checkPermissions("INCURRED", "edit", featuresRef.current)
    ) {
      return;
    }
    if (!dataDetail?._id || !ensureDetailSavedBeforeSubmit()) return;

    const targetId = dataDetail._id;
    try {
      if (
        !checkPermissions(
          "INCURRED",
          "edit",
          featuresRef.current,
        )
      ) {
        return;
      }
      await OrderApi.putSubmitOrderCost(targetId, 1);
      await reloadOrderCostSheet(targetId, "WAITING_APPROVE_L1");
    } catch (error: any) {
      messageApi.error(error?.message || "Chuyển duyệt cấp 1 thất bại");
    }
  };

  const handleReviewOrderCost = async (
    level: 1 | 2,
    reviewer: OrderCostReviewer,
  ) => {
    if (!dataDetail?._id) return false;
    if (
      !checkPermissions("INCURRED", "view", featuresRef.current)
    ) {
      return false;
    }
    if (
      (level === 1 && reviewer !== "sale") ||
      (level === 2 && reviewer !== "manager")
    ) {
      return false;
    }

    const payload = buildOrderCostReviewPayload(
      dataDetail.lines ?? [],
      reviewer,
    );
    if (!payload.items.length) {
      messageApi.warning("Chưa có dòng nào được chọn duyệt");
      return false;
    }

    const reviewPermissions = {
      canApprove: checkPermissions(
        "INCURRED",
        level === 1 ? "approve" : "approve_l2",
        featuresRef.current,
      ),
      canReject: checkPermissions(
        "INCURRED",
        "reject",
        featuresRef.current,
      ),
    };
    if (!isOrderCostReviewPayloadAllowed(payload, reviewPermissions)) {
      messageApi.warning(
        "Không có quyền thực hiện trạng thái duyệt đã chọn",
      );
      return false;
    }

    const targetId = dataDetail._id;
    const fallbackState = dataDetail.state;
    const shouldAdvance =
      level === 1 &&
      shouldAdvanceOrderCostReview(dataDetail.lines ?? [], reviewer);

    try {
      await OrderApi.patchReviewOrderCost(targetId, level, payload);
      let didAdvance = false;
      if (
        shouldAdvance &&
        checkPermissions(
          "INCURRED",
          "approve",
          featuresRef.current,
        )
      ) {
        await OrderApi.putSubmitOrderCost(targetId, 2);
        didAdvance = true;
      }
      await reloadOrderCostSheet(
        targetId,
        didAdvance ? "WAITING_APPROVE_L2" : fallbackState,
      );
      return true;
    } catch (error: any) {
      messageApi.error(error?.message || "Lưu duyệt thất bại");
      return false;
    }
  };

  return {
    dataDetail,
    loadingDetail,
    savingDetail,
    isSavingDetail,
    closeDetail,
    handleOnEditDetail,
    handleOnChangeDetail,
    handleSaveDetail,
    handleSubmitOrderCostLevel1,
    handleReviewOrderCost,
  };
};
